import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PageHeader from '@/components/feature/PageHeader';
import Button from '@/components/base/Button';
import { useAppData } from '@/store/AppDataContext';
import { serviceTypeMeta, type ServiceType } from '@/mocks/packages';

const SERVICE_TYPES: ServiceType[] = ['washOnly', 'ironOnly', 'washIron'];

interface FormData {
  name: string;
  serviceType: ServiceType;
  itemCount: string;
  validityDays: string;
  price: string;
  originalPrice: string;
}

interface FormErrors {
  name?: string;
  itemCount?: string;
  validityDays?: string;
  price?: string;
}

function blankForm(): FormData {
  return { name: '', serviceType: 'washIron', itemCount: '', validityDays: '30', price: '', originalPrice: '' };
}

export default function PackageFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { packages, addPackage, updatePackage } = useAppData();

  const existing = id ? packages.find((pkg) => pkg.id === id) : undefined;
  const isEdit = Boolean(existing);

  const [form, setForm] = useState<FormData>(() =>
    existing
      ? {
          name: existing.name,
          serviceType: existing.serviceType,
          itemCount: String(existing.itemCount),
          validityDays: String(existing.validityDays),
          price: String(existing.price),
          originalPrice: String(existing.originalPrice),
        }
      : blankForm()
  );
  const [errors, setErrors] = useState<FormErrors>({});

  const set = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = 'اسم الباقة مطلوب';
    if (!form.itemCount || Number(form.itemCount) < 1) newErrors.itemCount = 'عدد القطع يجب أن يكون أكبر من صفر';
    if (!form.validityDays || Number(form.validityDays) < 1) newErrors.validityDays = 'مدة الصلاحية يجب أن تكون أكبر من صفر';
    if (!form.price || Number(form.price) < 1) newErrors.price = 'السعر مطلوب';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    const original = Number(form.originalPrice);
    const data = {
      name: form.name.trim(),
      serviceType: form.serviceType,
      itemCount: Number(form.itemCount),
      validityDays: Number(form.validityDays),
      price: Number(form.price),
      originalPrice: original > 0 ? original : Number(form.price),
      active: existing ? existing.active : true,
    };
    if (isEdit && existing) {
      updatePackage(existing.id, data);
      navigate('/packages', { state: { toast: { type: 'success', message: `تم تحديث "${data.name}" بنجاح` } } });
    } else {
      addPackage(data);
      navigate('/packages', { state: { toast: { type: 'success', message: `تم إضافة "${data.name}" بنجاح` } } });
    }
  };

  return (
    <DashboardLayout title={isEdit ? 'تعديل الباقة' : 'إضافة باقة جديدة'}>
      <div className="mx-auto w-full max-w-[640px]">
        <PageHeader
          title={isEdit ? 'تعديل الباقة' : 'إضافة باقة جديدة'}
          subtitle={
            isEdit
              ? 'التعديل يسري على الاشتراكات الجديدة فقط ولا يؤثر على المشتركين الحاليين.'
              : 'الباقات تعمل بأولوية عادي فقط.'
          }
          backTo="/packages"
        />

        <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
          <div className="space-y-5">
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-foreground-800">
                اسم الباقة <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(event) => set('name', event.target.value)}
                placeholder="مثال: باقة العائلة"
                className={[
                  'h-12 w-full rounded-field border px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2',
                  errors.name
                    ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-100'
                    : 'border-background-300 focus:border-secondary-400 focus:ring-secondary-100',
                ].join(' ')}
              />
              {errors.name && <p className="mt-1.5 text-[12px] text-danger-600">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-medium text-foreground-800">نوع الخدمة</label>
              <div className="flex gap-2">
                {SERVICE_TYPES.map((type) => {
                  const meta = serviceTypeMeta[type];
                  const isActive = form.serviceType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => set('serviceType', type)}
                      className={[
                        'flex flex-1 flex-col items-center justify-center gap-1.5 rounded-field border px-3 py-3 text-center transition-all',
                        isActive
                          ? 'border-secondary-400 bg-secondary-50 text-secondary-700 ring-2 ring-secondary-100'
                          : 'border-background-200 text-foreground-500 hover:border-background-300 hover:bg-background-50',
                      ].join(' ')}
                    >
                      <span className={`flex h-8 w-8 items-center justify-center rounded-full ${isActive ? 'bg-secondary-100' : 'bg-background-100'}`}>
                        <i className={`${meta.icon} text-lg`} aria-hidden="true"></i>
                      </span>
                      <span className="text-[12px] font-medium">{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground-800">
                  عدد القطع <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number" min={1} value={form.itemCount}
                    onChange={(event) => set('itemCount', event.target.value)}
                    placeholder="20"
                    className={[
                      'h-12 w-full rounded-field border px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2',
                      errors.itemCount
                        ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-100'
                        : 'border-background-300 focus:border-secondary-400 focus:ring-secondary-100',
                    ].join(' ')}
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-foreground-400">قطعة</span>
                </div>
                {errors.itemCount && <p className="mt-1.5 text-[12px] text-danger-600">{errors.itemCount}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground-800">
                  مدة الصلاحية <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number" min={1} value={form.validityDays}
                    onChange={(event) => set('validityDays', event.target.value)}
                    placeholder="30"
                    className={[
                      'h-12 w-full rounded-field border px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2',
                      errors.validityDays
                        ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-100'
                        : 'border-background-300 focus:border-secondary-400 focus:ring-secondary-100',
                    ].join(' ')}
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-foreground-400">يوم</span>
                </div>
                {errors.validityDays && <p className="mt-1.5 text-[12px] text-danger-600">{errors.validityDays}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground-800">
                  السعر المخفض <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number" min={1} value={form.price}
                    onChange={(event) => set('price', event.target.value)}
                    placeholder="400"
                    className={[
                      'h-12 w-full rounded-field border px-4 pe-14 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2',
                      errors.price
                        ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-100'
                        : 'border-background-300 focus:border-secondary-400 focus:ring-secondary-100',
                    ].join(' ')}
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-foreground-400">ر.س</span>
                </div>
                {errors.price && <p className="mt-1.5 text-[12px] text-danger-600">{errors.price}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground-800">
                  السعر الأصلي (اختياري)
                </label>
                <div className="relative">
                  <input
                    type="number" min={0} value={form.originalPrice}
                    onChange={(event) => set('originalPrice', event.target.value)}
                    placeholder="500"
                    className="h-12 w-full rounded-field border border-background-300 px-4 pe-14 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100"
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-foreground-400">ر.س</span>
                </div>
                <p className="mt-1.5 text-[11px] text-foreground-400">يُستخدم لحساب نسبة الخصم المعروضة</p>
              </div>
            </div>

            <div className="rounded-lg bg-accent-50 p-3 text-[12px] text-accent-800">
              <i className="ri-information-line me-1.5 text-[14px]" aria-hidden="true"></i>
              الباقات تعمل دائماً بأولوية عادي فقط — لا يمكن إنشاء باقة مستعجلة.
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-background-100 pt-5 sm:flex-row">
            <Button variant="outline" size="lg" className="flex-1" onClick={() => navigate('/packages')}>
              إلغاء
            </Button>
            <Button variant="cta" size="lg" className="flex-1" icon="ri-save-line" onClick={submit}>
              حفظ
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}